import { useCallback, useEffect, useState, useRef } from "react";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { saveStoreData } from "../../features/login/LoginSlice"; // Adjust path as needed
// import { useNavigate } from "react-router-dom"; // Uncomment if you need navigation

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 500;

export const useAnalytics = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  // Redux Selectors
  const { urls } = useSelector((state) => state.vaultSlice || {});
  const { authData, storeId, isAdmin } = useSelector((state) => state.loginSlice || {});
  // State Management
  const [data, setData] = useState({
    lifetimeSales: {},
    tileMetrics: {},
    tableMetrics: { bestSellers: [] },
    trendMetrics: { period: [], orderValue: [], orderCount: [] },
    mapMetrics: [],
    dynamicMetrics: {
      statusMetrics: [],
      stateMetrics: [],
      buyerAppMetrics: [],
    },
  });

  const [loader, setLoader] = useState(false);
  const [key, setKey] = useState("orders");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState(null);
  const [viewBy, setViewBy] = useState("value"); // 'value' or 'count'
  const [selectedCategory, setSelectedCategory] = useState("status");
  const [chartType, setChartType] = useState("pie");
  const [topCategoryCount, setTopCategoryCount] = useState(5);
  const [selectedDateCategory, setSelectedDateCategory] =
    useState("all");

  // Store Selection State
  const [openSelectStore, setOpenSelectStore] = useState(false);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");

  // Refs
  const debounceTimer = useRef(null);

  // --- Date Handling ---
  const [dateRange, setDateRange] = useState([null, null]);

  const handlePeriodChange = (start, end) => {
    // const formattedStart = start ? start.toISOString() : null;
    // const formattedEnd = end ? end.toISOString() : null;

    setStartDate(start ? dayjs(start).toDate() : null);
    setEndDate(end ? dayjs(end).toDate() : null);
    setDateRange([start, end]);
  };

  const onDateRangeChange = (newValue) => {
    if (newValue) {
      const [start, end] = newValue;
      const adjustedStart = start ? dayjs(start).startOf("day") : null;
      const adjustedEnd = end ? dayjs(end).endOf("day") : null;

      setDateRange([adjustedStart, adjustedEnd]);

      const formattedStartDate = adjustedStart
        ? adjustedStart.format("ddd MMM DD YYYY HH:mm:ss")
        : null;
      const formattedEndDate = adjustedEnd
        ? adjustedEnd.format("ddd MMM DD YYYY HH:mm:ss")
        : null;

      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);
    } else {
      setDateRange([null, null]);
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleSelectChange = (value) => {
    setSelectedDateCategory(value);
    const now = dayjs();

    switch (value) {
      case "last 5-years":
        handlePeriodChange(
          now.subtract(5, "years").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 3-years":
        handlePeriodChange(
          now.subtract(3, "years").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 1-year":
        handlePeriodChange(
          now.subtract(1, "year").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 6-months":
        handlePeriodChange(
          now.subtract(6, "months").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 3-months":
        handlePeriodChange(
          now.subtract(3, "months").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 1-month":
        handlePeriodChange(
          now.subtract(1, "month").startOf("day"),
          now.endOf("day")
        );
        break;
      case "last 1-week":
        handlePeriodChange(
          now.subtract(7, "days").startOf("day"),
          now.endOf("day")
        );
        break;
      case "today":
        handlePeriodChange(now.startOf("day"), now.endOf("day"));
        break;
      case "custom":
        handlePeriodChange(null, null);
        break;
      case "all":
      default:
        handlePeriodChange(null, null);
        break;
    }
  };

  // --- API Calls ---

  useEffect(() => {
    const getAnalytics = async () => {
      if (!authData) return;

      setLoader(true);
      setError(null);

      try {
        const baseUrl = urls?.APPLICATION_URL ? urls.APPLICATION_URL : "";
        let url = `${baseUrl}${endpoint.GET_ANALYTICS || "/api/analytics"}?`; // Fallback endpoint if undefined

        if (startDate) url += `&from=${startDate}`;
        if (endDate) url += `&to=${endDate}`;

        const result = await apiHandler({
          url,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        if (result?.data) {
          setData({
            lifetimeSales: result.data.lifetimeSales || {},
            tileMetrics: result.data.tileMetrics || {},
            tableMetrics: result.data.tableMetrics || { bestSellers: [] },
            trendMetrics: result.data.trendMetrics || {
              period: [],
              orderValue: [],
              orderCount: [],
            },
            mapMetrics: result.data.mapMetrics || [],
            dynamicMetrics: result.data.dynamicMetrics || {
              statusMetrics: [],
              stateMetrics: [],
              buyerAppMetrics: [],
            },
          });
        }
      } catch (error) {
        //console.error(error);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoader(false);
      }
    };

    // Initialize defaults on mount if needed
    if (!startDate && !endDate && selectedDateCategory === "last 1-year") {
      // Optional: trigger initial load based on default date
      handleSelectChange("last 1-year");
    } else {
      getAnalytics();
    }
  }, [startDate, endDate, authData, storeId, urls]);

  // --- Store Management ---

  const fetchStores = async (searchQuery = "") => {
   if (!authData || !isAdmin) return; 
    
    try {
      // Note: Ensure this URL is correct or comes from your endpoint config
      const response = await apiHandler({
        url: `https://ondcidentitybackend.clouddeploy.in/api/v1/tenant/getMSNTenants?iPageSize=${100}&sSearchByName=${searchQuery}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (response?.data?.statusCode === 200) {
        const storeList = response?.data?.data || [];
        setStores(storeList);
      }
    } catch (err) {
      //console.log("Error fetching stores", err);
    }
  };

  const debouncedSearch = useCallback(
    (searchQuery) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
        debounceTimer.current = setTimeout(() => {
          fetchStores(searchQuery);
        }, DEBOUNCE_DELAY);
      }
    },
    [authData, dispatch, storeId]
  );

  useEffect(() => {
    if (isAdmin) {
      fetchStores(search);
    }
  }, [isAdmin, search]);


  const handleStoreSearch = (e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  };

  const handleChangeStore = (store) => {
    dispatch(
      saveStoreData({
        storeId: store.id,
        apiKey: store.api_key,
      })
    );
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const handleRefresh = useCallback(async () => {
    if (!authData) return;

    setLoader(true);
    setError(null);

    try {
      const baseUrl = urls?.APPLICATION_URL ? urls.APPLICATION_URL : "";
      let url = `${baseUrl}${endpoint.GET_ANALYTICS || "/api/analytics"}?`;

      if (startDate) url += `&from=${startDate}`;
      if (endDate) url += `&to=${endDate}`;

      const result = await apiHandler({
        url,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result?.data) {
        setData({
          lifetimeSales: result.data.lifetimeSales || {},
          tileMetrics: result.data.tileMetrics || {},
          tableMetrics: result.data.tableMetrics || { bestSellers: [] },
          trendMetrics: result.data.trendMetrics || {
            period: [],
            orderValue: [],
            orderCount: [],
          },
          mapMetrics: result.data.mapMetrics || [],
          dynamicMetrics: result.data.dynamicMetrics || {
            statusMetrics: [],
            stateMetrics: [],
            buyerAppMetrics: [],
          },
        });
      }
    } catch (error) {
      //console.error(error);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoader(false);
    }
  }, [startDate, endDate, authData, storeId, urls, dispatch]);

  return {
    loader,
    key,
    setKey,
    data,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handlePeriodChange,
    error,
    viewBy,
    setViewBy,
    handleSelectChange,
    onDateRangeChange,
    dateRange,
    selectedDateCategory,
    selectedCategory,
    setSelectedCategory,
    chartType,
    setChartType,
    topCategoryCount,
    setTopCategoryCount,
    openSelectStore,
    setOpenSelectStore,
    handleChangeStore,
    stores,
    search,
    setSearch,
    handleStoreSearch,
    handleRefresh,
  };
};
